<template>
    <main>
        <div v-if="working" class="text-center py-5">
            <LoadingSpinner />
        </div>
        <template v-else>
            <!-- Section 1 : Factures à régler -->
            <UnpaidGroups
                v-if="unpaid_groups.length > 0"
                :groups="unpaid_groups"
                :group-totals="group_totals"
                @pay="openPayModal"
                @download="downloadOne"
            />
            
            <!-- Section 2 : Toutes mes factures -->
            <HeaderTab :title="$t('invoice.toutes_mes_factures')" :icon="['fal', 'file-invoice']">
                <template #actions>
                    <div class="d-flex a    lign-items-center" style="gap:0.5rem">
                        <b-form-select
                            v-if="year_options.length > 0"
                            v-model="filter_year"
                            :options="year_options"
                            size="sm"
                            style="width:auto"
                            class="d-none d-md-block"
                        />
                        <b-form-select
                            v-if="author_options.length > 2"
                            v-model="filter_author"
                            :options="author_options"
                            size="sm"
                            style="width:auto;max-width:180px"
                            class="d-none d-md-block"
                        />
                        <b-form-select
                            v-if="client_options.length > 2"
                            v-model="filter_client"
                            :options="client_options"
                            size="sm"
                            style="width:auto;max-width:180px"
                            class="d-none d-md-block"
                        />
                        <b-button
                            variant="secondary"
                            size="sm"
                            :disabled="downloading || filtered_invoices.length === 0"
                            @click="downloadAll"
                        >
                        <span class="d-none d-md-inline">{{ $t('invoice.tout_telecharger') }} </span>
                        <font-awesome-icon :icon="['fal', downloading ? 'spinner' : 'download']" :pulse="downloading" />
                    </b-button>
                    
                </div>
            </template>
        </HeaderTab>
        
        <div id="content">
            <div class="container-fluid">
                <!-- Filtres : visibles uniquement sur mobile -->
                <div v-if="year_options.length > 0 || author_options.length > 2 || client_options.length > 2" class="d-flex d-md-none align-items-center flex-wrap pt-2 pb-1" style="gap:0.5rem">
                    <b-form-select
                        v-if="year_options.length > 0"
                        v-model="filter_year"
                        :options="year_options"
                        size="sm"
                        style="width:auto"
                    />
                    <b-form-select
                        v-if="author_options.length > 2"
                        v-model="filter_author"
                        :options="author_options"
                        size="sm"
                        style="width:auto;max-width:180px"
                    />
                    <b-form-select
                        v-if="client_options.length > 2"
                        v-model="filter_client"
                        :options="client_options"
                        size="sm"
                        style="width:auto;max-width:180px"
                    />
                </div>
                <div class="row">
                    <div class="col-12">
                        <div v-if="enable_add_mandate" class="alert alert-info mb-3">
                            {{ $t('gocardless.add_mandate') }}<br>
                            <button :disabled="loading_add_mandate" class="btn btn-primary btn-sm mt-2" type="button" @click.prevent="add_mandate()">
                                <font-awesome-icon v-if="loading_add_mandate" :icon="['far', 'spinner']" spin />
                                {{ $t('global.ajouter') }}
                            </button>
                        </div>
                        
                        <EmptyState v-if="filtered_invoices.length === 0" />
                        <template v-else>
                            <!-- Vue cards : mobile uniquement -->
                            <div class="d-md-none">
                                <div
                                v-for="inv in filtered_invoices"
                                :key="'card-' + inv.invoice_id"
                                class="border-bottom py-3"
                                >
                                <div class="d-flex align-items-start" style="min-width:0">
                                    <div class="flex-fill" style="min-width:0;overflow:hidden">
                                        <div class="d-flex align-items-baseline mb-1">
                                            <span class="font-weight-bold mr-2">#{{ inv.invoice_num }}</span>
                                            <small class="text-muted">{{ inv.invoice_date_formatted }}</small>
                                        </div>
                                        <div v-if="inv.horses_label" class="small text-truncate text-muted mb-1" :title="inv.horses_label">{{ inv.horses_label }}</div>
                                        <div class="d-flex align-items-baseline" style="gap:0.75rem">
                                            <span
                                            v-if="inv.duedate_balance > 0"
                                            class="small font-weight-bold text-danger"
                                            >{{ $t('invoice.a_regler') }} {{ inv.balance_formatted }}</span>
                                        </div>
                                    </div>
                                    <div class="d-flex align-items-center ml-2 flex-shrink-0">
                                        <b-button variant="link" size="sm" class="p-0" @click="downloadOne(inv)">
                                            <font-awesome-icon :icon="['fal', 'download']" />
                                        </b-button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <!-- Vue tableau : desktop uniquement -->
                        <div class="d-none d-md-block table-responsive">
                            <table class="table table-hover mb-0">
                                <thead class="small text-muted">
                                    <tr>
                                        <th class="text-nowrap">{{ $t('table.invoices.invoice_date') }}</th>
                                        <th>#</th>
                                        <th>{{ $t('invoice.chevaux') }}</th>
                                        <th class="text-right text-nowrap">{{ $t('invoice.a_regler') }}</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="inv in filtered_invoices" :key="inv.invoice_id">
                                        <td class="small text-nowrap">{{ inv.invoice_date_formatted }}</td>
                                        <td class="small text-muted text-nowrap">#{{ inv.invoice_num }}</td>
                                        <td class="small text-truncate" style="max-width:300px">{{ inv.horses_label }}</td>
                                        <td class="small text-right text-nowrap" :class="inv.duedate_balance > 0 ? 'text-danger font-weight-bold' : 'text-muted'">
                                            {{ inv.duedate_balance > 0 ? inv.balance_formatted : '—' }}
                                        </td>
                                        <td class="text-right text-nowrap">
                                            <b-button variant="link" size="sm" class="p-0" @click="downloadOne(inv)">
                                                <font-awesome-icon :icon="['fal', 'download']" />
                                            </b-button>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<b-modal ref="modal" hide-footer>
    <template v-slot:modal-title>
        {{ $t("action.online_paiement") }}
    </template>
    
    <div v-if="!modal_loaded">
        <LoadingSpinner class="col-12" />
    </div>
    
    <div v-if="modal_loaded">
        
        <template v-if="marketplace == 'mangopay'">
            
            <RequiredInfosUser 
            v-if="require_infos"
            :callback="prepareDuedate"/>
            
            <form v-else @submit.prevent="payDuedates">
                
                <div class="row">
                    <div class="col-12" v-if="form_message !== ''">
                        <ErrorAlert :messageI18n="form_message" />
                    </div>
                    
                    <AmountsToPay :amounts="dates" :now-label="$t('marketplace.au_plus_vite')" />
                    
                    <div class="col-12">
                        <label>{{ $t('marketplace.choix_compte_bancaire') }}</label>
                        <e-select
                        v-model="compte"
                        track-by="id"
                        label="formatted_libelle"
                        :placeholder="getTrad('marketplace.choix_compte_bancaire')"
                        :selectedLabel="getTrad('global.selected_label')"
                        :options="formattedAccount"
                        :allow-empty="true"
                        :show-labels="false"
                        :sortable="false"
                        :loading="loadingAccount"
                        :class="{ 'is-invalid': errors && errors.indexOf('account') > -1 }"
                        ></e-select>
                    </div>
                    
                </div>
                <b-button class="mt-3" v-if="compte != null" block @click.prevent="payDuedates">{{ $t("marketplace.payer") }}</b-button>
            </form>
        </template>
        <template v-else-if="marketplace == 'monetico'">
            <form @submit.prevent="payDuedates">
                
                <div class="row">
                    <div class="col-12" v-if="form_message !== ''">
                        <ErrorAlert :messageI18n="form_message" />
                    </div>
                    
                    <AmountsToPay :amounts="dates" :now-label="$t('marketplace.au_plus_vite')" />
                </div>
                <b-button class="mt-3" block @click.prevent="payDuedates">{{ $t("marketplace.payer") }}</b-button>
            </form>
            
            <form method="POST" :action="monetico_infos.url" id="redirectForm" class="d-none">
                <input type="hidden" name="version" :value="monetico_infos.version">
                <input type="hidden" name="TPE" :value="monetico_infos.TPE">
                <input type="hidden" name="date" :value="monetico_infos.date">
                <input type="hidden" name="montant" :value="monetico_infos.montant">
                <input type="hidden" name="reference" :value="monetico_infos.reference">
                <input type="hidden" name="MAC" :value="monetico_infos.MAC">
                <input type="hidden" name="url_retour" :value="monetico_infos.url_retour">
                <input type="hidden" name="url_retour_ok" :value="monetico_infos.url_retour_ok">
                <input type="hidden" name="url_retour_err" :value="monetico_infos.url_retour_err">
                <input type="hidden" name="lgue" :value="monetico_infos.lgue">
                <input type="hidden" name="societe" :value="monetico_infos.societe">
                <input type="hidden" name="texte-libre" :value="monetico_infos.textelibre">
                <input type="hidden" name="mail" :value="monetico_infos.mail">
                <button id="button_go_monetico" onclick="">valider</button>
            </form>
            
            
            
        </template>
        <template v-else-if="marketplace == 'atos'">
            <form @submit.prevent="payDuedates">
                
                <div class="row">
                    <div class="col-12" v-if="form_message !== ''">
                        <ErrorAlert :messageI18n="form_message" />
                    </div>
                    
                    <AmountsToPay :amounts="dates" :now-label="$t('marketplace.au_plus_vite')" />
                </div>
                <b-button class="mt-3" block @click.prevent="payDuedates">{{ $t("marketplace.payer") }}</b-button>
            </form>
            
            <!-- Formulaire caché pour redirection vers Atos -->
            <form method="post" :action="atos_infos.url" id="redirectForm" class="d-none">
                <input type="hidden" name="Data" :value="atos_infos.data">
                <input type="hidden" name="InterfaceVersion" value="HP_2.14">
                <input type="hidden" name="Seal" :value="atos_infos.seal">
                <button id="button_go_atos" onclick="">valider</button>
            </form>
        </template>
        <template v-else-if="marketplace == 'paybox'">
            <form @submit.prevent="payDuedates">
                
                <div class="row">
                    <div class="col-12" v-if="form_message !== ''">
                        <ErrorAlert :messageI18n="form_message" />
                    </div>
                    
                    <AmountsToPay :amounts="dates" :now-label="$t('marketplace.au_plus_vite')" />
                </div>
                <b-button class="mt-3" block @click.prevent="payDuedates">{{ $t("marketplace.payer") }}</b-button>
            </form>
            
            <!-- Formulaire caché pour redirection vers Paybox -->
            <form method="POST" :action="paybox_infos.url" id="redirectForm" class="d-none">
                <template v-for="(value, key) in paybox_infos">
                    <input v-if="key !== 'url'" :key="key" type="hidden" :name="key" :value="value">
                </template>
                <button id="button_go_paybox" onclick="">{{ $t('global.valider') }}</button>
            </form>
        </template>
        
        <template v-if="marketplace == 'sogecommerce'">
            <form @submit.prevent="payDuedates">
                
                <b-button class="mt-3" block @click.prevent="payDuedates">{{ $t("marketplace.payer") }}</b-button>
            </form>
            
            <!-- Formulaire caché pour redirection vers Sogecommerce -->
            <form method="POST" :action="sogecommerce_infos.url" id="redirectForm" class="d-none">
                <template v-for="(value, key) in sogecommerce_infos">
                    <input v-if="key !== 'url'" :key="key" type="hidden" :name="key" :value="value">
                </template>
                <button id="button_go_sogecommerce" onclick="">{{ $t('global.valider') }}</button>
            </form>
        </template>
        
        <template v-else-if="marketplace == 'payplug'">
            <form @submit.prevent="payDuedates">
                
                <div class="row">
                    <div class="col-12" v-if="form_message !== ''">
                        <ErrorAlert :messageI18n="form_message" />
                    </div>
                    
                    <AmountsToPay :amounts="dates" :now-label="$t('marketplace.au_plus_vite')" />
                </div>
                <b-button class="mt-3" block @click.prevent="payDuedates">{{ $t("marketplace.payer") }}</b-button>
            </form>
        </template>
        
        <template v-else-if="marketplace == 'cawl'">
            <form @submit.prevent="payDuedates">
                
                <div class="row">
                    <div class="col-12" v-if="form_message !== ''">
                        <ErrorAlert :messageI18n="form_message" />
                    </div>
                    
                    <AmountsToPay :amounts="dates" :now-label="$t('marketplace.au_plus_vite')" />
                </div>
                <b-button class="mt-3" block @click.prevent="payDuedates">{{ $t("marketplace.payer") }}</b-button>
            </form>
        </template>
    </div>
</b-modal>

</main>
</template>


<script type="text/javascript">
import Invoice from '@/mixins/Invoice.js'
import Navigation from '@/mixins/Navigation.js'
import Marketplace from '@/mixins/Marketplace.js'
import Gocardless from '@/mixins/Gocardless.js'
import Tools from 'GroomyRoot/mixins/Tools.js'
import Common from 'GroomyRoot/assets/js/common.js'

export default {
    name: "InvoiceAddressed",
    mixins:[Invoice, Navigation, Marketplace, Gocardless, Tools],
    data () {
        return {
            all_invoices: [],
            downloading: false,
            filter_year: null,
            filter_client: null,
            filter_author: null,
            group_totals: {},
            
            url_boutique: null,
            marketplace: 'mangopay',
            require_infos: true,
            working: false,
            modal_loaded: false,
            form_message: '',
            errors: [],
            accounts: [],
            dates: [],
            go_atos: false,
            go_paybox: false,
            atos_infos: {},
            paybox_infos: {},
            sogecommerce_infos: {},
            monetico_infos: {},
            compte: null,
            enable_add_mandate: false,
            loading_add_mandate: false,
            duedates: []
        }
    },
    
    computed: {
        year_options() {
            const years = [...new Set(this.all_invoices.map(inv => inv.invoice_year).filter(Boolean))].sort((a, b) => b - a)
            return years.map(y => ({ value: y, text: String(y) }))
        },
        
        client_options() {
            const all = { value: null, text: this.$t('invoice.tous_clients') }
            const names = [...new Set(this.all_invoices.map(inv => inv.tiers_rs).filter(Boolean))].sort()
            return [all, ...names.map(n => ({ value: n, text: n }))]
        },
        
        author_options() {
            const all = { value: null, text: this.$t('invoice.tous_emetteurs') }
            const names = [...new Set(this.all_invoices.map(inv => inv.author_rs).filter(Boolean))].sort()
            return [all, ...names.map(n => ({ value: n, text: n }))]
        },
        
        filtered_invoices() {
            return this.all_invoices
            .filter(inv => {
                if (this.filter_year && inv.invoice_year !== this.filter_year) return false
                if (this.filter_client && inv.tiers_rs !== this.filter_client) return false
                if (this.filter_author && inv.author_rs !== this.filter_author) return false
                return true
            })
            .sort((a, b) => new Date(b.invoice_date) - new Date(a.invoice_date))
        },
        
        has_unpaid() {
            return this.filtered_invoices.some(inv => inv.duedate_balance > 0)
        },
        
        has_unpaid_eur() {
            return this.filtered_invoices.some(inv => inv.duedate_balance > 0 && inv.currency === 'EUR')
        },
        
        unpaid_groups() {
            // Groupe les factures impayées par couple (author_rs, tiers_rs)
            const map = new Map()
            for (const inv of this.all_invoices.filter(i => i.duedate_balance > 0)) {
                const key = `${inv.author_rs}||${inv.tiers_rs}`
                if (!map.has(key)) {
                    map.set(key, { author_rs: inv.author_rs, tiers_rs: inv.tiers_rs, currency: inv.currency, items: [] })
                }
                map.get(key).items.push(inv)
            }
            return [...map.values()]
        },
        
        formattedAccount() {
            let formattedAccount = []
            for (let i = 0; i < this.accounts.length; i++) {
                let temp = this.accounts[i]
                temp.formatted_libelle = this.accounts[i].libelle + " (" + this.accounts[i].numero.replace("<br>", ", ") + ")"
                if(temp.mandate != null && (temp.mandate.status == "SUBMITTED" || temp.mandate.status == "ACTIVE")) {
                    formattedAccount.push(temp)
                }
            }
            return formattedAccount
        }
    },
    
    watch: {
        formattedAccount(val) {
            if(val.length == 1 && this.compte == null) {
                this.compte = val[0]
            }
        }
    },
    
    async mounted() {
        this.init_component()
    },
    
    methods: {
        /**
         * Charge et prépare les données de la vue : comptes bancaires, factures adressées
         * dédoublées par facture, pré-formatage des montants (priceFormat étant asynchrone,
         * rien n'est formaté dans les templates), puis totaux par groupe émetteur/client.
         * @returns {Promise<void>}
         */
        async init_component() {
            this.working = true
            
            if(this.$store.state.userAccess.hasPaymentGocardlessAccess) {
                const bank_accounts = await this.getBankAccounts()
                if(!bank_accounts) {
                    this.enable_add_mandate = true
                }
            }
            
            try {
                // 1. Récupération parallèle des factures à payer et des factures soldées
                const [toPay, sold] = await Promise.all([
                    this.getAddressedInvoicesToPay(),
                    this.getAddressedInvoicesSold(),
                ])
                
                // 2. Déduplication par invoice_id : en cas de doublon, on préfère l'entrée avec balance > 0
                const byId = new Map()
                for (const item of [...(toPay || []), ...(sold || [])]) {
                    const inv = item.invoice
                    if (!inv) continue
                    const id = inv.invoice_id
                    if (!byId.has(id) || (item.duedate_balance > 0 && byId.get(id).duedate_balance <= 0)) {
                        byId.set(id, item)
                    }
                }
                
                // 3. Formatage des données — priceFormat est async, on les pré-calcule ici
                //    pour éviter des appels asynchrones dans les templates et computed
                this.all_invoices = await Promise.all([...byId.values()].map(async item => {
                    const inv = item.invoice
                    const year = inv.invoice_date ? parseInt(inv.invoice_date.substring(0, 4)) : null
                    
                    const horses = []
                    if (Array.isArray(inv.details)) {
                        for (const d of inv.details) {
                            const nom = d.horse && d.horse.horse_nom
                            if (nom && !horses.includes(nom)) horses.push(nom)
                        }
                    }
                    
                    return {
                        invoice_id:             inv.invoice_id,
                        invoice_num:            inv.invoice_num,
                        invoice_date:           inv.invoice_date,
                        invoice_year:           year,
                        invoice_date_formatted: this.formatDate(inv.invoice_date),
                        tiers_rs:               inv.tiers ? inv.tiers.tiers_rs : '',
                        author_rs:              inv.author.tiers_rs,
                        horses_label:           horses.join(', '),
                        currency:               inv.author.tiers_currency,
                        duedate_balance:        item.duedate_balance || 0,
                        balance_formatted:      item.duedate_balance > 0 ? await Common.priceFormat(item.duedate_balance, inv.author.tiers_currency, true) : '',
                        _raw:                   item,
                    }
                }))
                
                if (this.year_options.length > 0) {
                    this.filter_year = this.year_options[0].value
                }
                
                // 4. Pré-calculer les totaux formatés par groupe émetteur/client pour UnpaidGroups
                // Cumul en centimes entiers : additionner les montants en euros laisserait
                // traîner un résidu de virgule que priceFormat tronquerait à un centime près.
                const totals = {}
                for (const inv of this.all_invoices.filter(i => i.duedate_balance > 0)) {
                    const key = `${inv.author_rs}||${inv.tiers_rs}`
                    if (totals[key] === undefined) {
                        totals[key] = { cents: 0, currency: inv.currency }
                    }
                    totals[key].cents += Math.round(inv.duedate_balance * 100)
                }
                const totalsFormatted = {}
                for (const [key, total] of Object.entries(totals)) {
                    totalsFormatted[key] = await Common.priceFormat(total.cents / 100, total.currency, true)
                }
                this.group_totals = totalsFormatted
            } catch(e) {
                this.failureToast("toast.info_lecture_failed")
                console.error("InvoiceAddressed::init_component => Error:", e)
            } finally {
                this.working = false
            }
        },
        
        openPayModal(rawItems) {
            const config = JSON.parse(this.getConfig("ca_config"))
            this.duedates = rawItems
            if(config.use_credit_invoice === false) {
                this.duedates = this.duedates.filter(elem => elem.duedate_amount > 0)
                if(this.duedates.length == 0) {
                    this.failureToast("toast.no_duedate_to_pay")
                    return
                }
            }
            this.$refs['modal'].show()
            this.getMarketplaceTypeLocal()
        },
        
        payAll() {
            const unpaid = this.filtered_invoices
            .filter(inv => inv.duedate_balance > 0 && inv.currency === 'EUR')
            .map(inv => inv._raw)
            if (unpaid.length > 0) this.openPayModal(unpaid)
        },
        
        downloadAll() {
            if (!this.filtered_invoices.length) return
            this.downloading = true
            this.getAddressedPdfInvoice(this.filtered_invoices.map(inv => ({
                invoice_id:   inv.invoice_id,
                invoice_num:  inv.invoice_num,
                invoice_date: inv.invoice_date,
            })))
            setTimeout(() => { this.downloading = false }, 3000)
        },
        
        downloadOne(inv) {
            this.getAddressedPdfInvoice([{
                invoice_id:   inv.invoice_id,
                invoice_num:  inv.invoice_num,
                invoice_date: inv.invoice_date,
            }])
        },
        
        async getMarketplaceTypeLocal(){
            
            let marketplace = await this.getMarketplaceType()
            this.marketplace = marketplace.retour.type
            if(this.marketplace == 'monetico')
            {
                await this.prepareDuedate()
            }
            else if(this.marketplace == 'atos')
            {
                await this.prepareDuedate()
            }
            else if(this.marketplace == 'paybox')
            {
                await this.prepareDuedate()
            }
            else if(this.marketplace == 'payplug')
            {
                await this.prepareDuedate()
            }
            else if(this.marketplace == 'cawl')
            {
                await this.prepareDuedate()
            }
            this.modal_loaded = true
        },
        
        /**
         * Prépare le paiement des échéances sélectionnées : initialise le moyen de paiement
         * selon la marketplace, puis construit le récapitulatif des montants à régler.
         * @returns {Promise<void>}
         */
        async prepareDuedate() {
            
            this.modal_loaded = true
            this.require_infos = false
            
            if(this.marketplace == 'mangopay')
            {
                this.loadPaymentMethod()
            }
            else if(this.marketplace == 'monetico')
            {
                //
            }
            else if(this.marketplace == 'atos')
            {
                //
            }
            else if(this.marketplace == 'paybox')
            {
                //
            }
            
            this.modal_loaded = false
            
            this.dates = await this.buildAmountsToPay(this.duedates, {
                dateOf:     duedate => duedate.duedate_date,
                // duedate_balance arrive en euros : repasser en centimes entiers avant de cumuler
                centsOf:    duedate => Math.round(duedate.duedate_balance * 100),
                currencyOf: duedate => duedate.invoice.author.tiers_currency
            })
            
            this.modal_loaded = true
        },
        
        async payDuedates(){
            
            let duedates_id = this.duedates.map(duedate => {
                return duedate.duedate_id
            })
            
            if(this.marketplace == 'mangopay')
            {
                await this.markToPaymentDuedates(duedates_id, this.compte.id)
                this.$refs['modal'].hide()
                this.init_component()
            }
            else if(this.marketplace == 'monetico')
            {
                let url_paiment = await this.redirectBoutique(duedates_id)
                
                if(url_paiment != null)
                {
                    url_paiment.retour.url = url_paiment.retour.url.replace("texte-libre", "textelibre")
                    this.monetico_infos = this.URLToArray(url_paiment.retour.url)
                    this.monetico_infos.url = url_paiment.retour.url.substring(0, url_paiment.retour.url.indexOf('?'))
                    
                    // A voir pour améliorer, le nextick ne fonctionne pas, ou alors j'arrive pas à l'utiliser correctement
                    setTimeout(() => document.querySelector("#button_go_monetico").click(), 0)
                }
                
            }
            else if(this.marketplace == 'atos')
            {
                let datas_payment = await this.redirectBoutique(duedates_id)
                if(datas_payment != null && datas_payment.retour.url != null)
                {
                    
                    this.atos_infos = {
                        url: datas_payment.retour.url,
                        data: datas_payment.retour.data,
                        seal: datas_payment.retour.key,
                    }
                    
                    //A voir pour améliorer, le nextick ne fonctionne pas, ou alors j'arrive pas à l'utiliser correctement
                    setTimeout(() => document.querySelector("#button_go_atos").click(), 0)
                }
            }
            else if(this.marketplace == 'paybox')
            {
                let datas_payment = await this.redirectBoutique(duedates_id)
                
                if(datas_payment != null && datas_payment.retour.url != null)
                {
                    this.paybox_infos =  datas_payment.retour
                    
                    //A voir pour améliorer, le nextick ne fonctionne pas, ou alors j'arrive pas à l'utiliser correctement
                    setTimeout(() => document.querySelector("#button_go_paybox").click(), 0)
                }
            }
            else if(this.marketplace == 'sogecommerce') 
            {
                let datas_payment = await this.redirectBoutique(duedates_id)
                
                if(datas_payment != null && datas_payment.retour.url != null)
                {
                    this.sogecommerce_infos =  datas_payment.retour
                    if(this.sogecommerce_infos.vads_amount == 0) {
                        this.failureToast("toast.contract_montant_zero")
                        this.$refs.modal.hide()
                        return false
                    }
                    
                    //A voir pour améliorer, le nextick ne fonctionne pas, ou alors j'arrive pas à l'utiliser correctement
                    setTimeout(() => document.querySelector("#button_go_sogecommerce").click(), 0)
                }
            }
            else if(this.marketplace == 'payplug')
            {
                let datas_payment = await this.redirectBoutique(duedates_id)
                
                if(datas_payment != null && datas_payment.retour.url != null)
                {
                    if(datas_payment.retour.amount == 0) {
                        this.failureToast("toast.contract_montant_zero")
                        this.$refs.modal.hide()
                        return false
                    }
                    
                    location.replace(datas_payment.retour.url)
                }
            }
            else if(this.marketplace == 'cawl')
            {
                let datas_payment = await this.redirectBoutique(duedates_id)
                
                if(datas_payment != null && datas_payment.retour.url != null)
                {
                    if(datas_payment.retour.amount == 0) {
                        this.failureToast("toast.contract_montant_zero")
                        this.$refs.modal.hide()
                        return false
                    }
                    
                    location.replace(datas_payment.retour.url)
                }
            }
        },
        
        async loadPaymentMethod() {
            this.loadingAccount = true
            this.working = true
            
            this.getPaymentMethod(true)
            .then(r => {
                this.accounts = r.retour
                this.loadingAccount = false
                this.working = false
            })
            .catch(e => {
            })
        },
        
        URLToArray(url) {
            const request = {}
            const search = url.indexOf('?') !== -1 ? url.substring(url.indexOf('?') + 1) : url
            new URLSearchParams(search).forEach((value, key) => { request[key] = value })
            return request
        },
        
        async add_mandate() {
            this.loading_add_mandate = true
            const mandate = await this.addMandate()
            if(mandate) {
                window.location = mandate.redirect_url
            } else {
                this.loading_add_mandate = false
            }
        },
    },
    
    components: {
        LoadingSpinner: () => import('GroomyRoot/components/Logos/LoadingSpinner_35'),
        HeaderTab: () => import('@/components/HeaderTab'),
        EmptyState: () => import('@/components/EmptyState'),
        UnpaidGroups: () => import('@/components/Invoice/UnpaidGroups'),
        AmountsToPay: () => import('@/components/Payment/AmountsToPay'),
        RequiredInfosUser: () => import('GroomyRoot/components/Marketplace/RequiredInfosUser'),
    }
}
</script>
